import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Disc, ArrowLeft, Activity } from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { rpc, RpcError } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ToolHeader } from '@/components/common/ToolHeader';
import { SessionSelector } from '@/components/sector/SessionSelector';
import { WizardStepper } from '@/components/sector/WizardStepper';
import { SessionRemovedAnalysisDialog } from '@/components/analysis/SessionRemovedAnalysisDialog';
import { useWizardState } from '@/hooks/useWizardState';
import type {
  SessionSummary,
  ListSessionsResponse,
  ListAnalysisTracksResponse,
  SessionDetailResponse,
  AnalyzeBrakingResponse,
  BrakingZoneData,
  BrakingLapEntry,
  AnalyzeResponse,
  GetAnalysisResponse,
  AnalysisRecord,
} from '@/types/telemetry';
import { formatDynamicCornerLabel, type TrackShape, type TrackAnnotation } from '@/lib/spline';

import { BrakingMap } from './BrakingMap';
import { GGDiagram } from './GGDiagram';
import { BrakingChart } from './BrakingChart';
import { BrakingStatsCard } from './BrakingStatsCard';
import { BrakingLapTable } from './BrakingLapTable';
import { BrakingOverallHeader } from './BrakingOverallHeader';

interface BrakingAnalysisProps {
  trackName?: string;
}

export function BrakingAnalysis({ trackName }: BrakingAnalysisProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();
  const wizardSteps = [
    t('braking.wizardChooseTrack'),
    t('braking.wizardSelect'),
    t('braking.wizardResults'),
  ];

  const [wizardState, updateWizardState] = useWizardState({
    step: trackName ? 1 : 0,
    draftTrack: undefined as string | undefined,
    selectedIds: [] as string[],
    driverFilter: 'all',
    weatherFilter: 'all',
    activeZone: 0,
  });

  const {
    step,
    draftTrack,
    selectedIds: selectedIdsArr,
    driverFilter,
    weatherFilter,
    activeZone,
  } = wizardState;
  const selectedIds = new Set(selectedIdsArr);

  const setStep = useCallback(
    (newStep: number | ((prev: number) => number)) => {
      updateWizardState(prev => {
        const nextStep = typeof newStep === 'function' ? newStep(prev.step) : newStep;
        return { step: nextStep };
      }, false);
    },
    [updateWizardState]
  );

  const setDraftTrack = useCallback(
    (track: string | undefined) => updateWizardState({ draftTrack: track }),
    [updateWizardState]
  );

  const setSelectedIds = useCallback(
    (ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      updateWizardState(prev => {
        const nextIds = typeof ids === 'function' ? ids(new Set(prev.selectedIds)) : ids;
        return { selectedIds: [...nextIds] };
      });
    },
    [updateWizardState]
  );

  const setDriverFilter = useCallback(
    (filter: string) => updateWizardState({ driverFilter: filter }),
    [updateWizardState]
  );

  const setWeatherFilter = useCallback(
    (filter: string) => updateWizardState({ weatherFilter: filter }),
    [updateWizardState]
  );

  const setActiveZone = useCallback(
    (zone: number) => updateWizardState({ activeZone: zone }),
    [updateWizardState]
  );

  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [availableTracks, setAvailableTracks] = useState<string[]>([]);
  const [trackId, setTrackId] = useState('');

  const [analysisResult, setAnalysisResult] = useState<AnalyzeBrakingResponse | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<string | undefined>(
    draftTrack || trackName || undefined
  );
  const [loading, setLoading] = useState(!trackName);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [sessionRemovedAnalysis, setSessionRemovedAnalysis] = useState<AnalysisRecord | null>(null);

  // Active & comparison lap selection
  const [activeLap, setActiveLap] = useState<BrakingLapEntry | null>(null);
  const [comparisonLap, setComparisonLap] = useState<BrakingLapEntry | null>(null);

  // Scrubbing index synchronized across GGDiagram, BrakingChart, and BrakingMap
  const [hoveredSampleIndex, setHoveredSampleIndex] = useState<number | null>(null);

  // Track map geometry
  const [trackShape, setTrackShape] = useState<TrackShape | null>(null);
  const [trackAnnotations, setTrackAnnotations] = useState<TrackAnnotation[]>([]);

  const zones = (analysisResult?.zones || []).map((z, idx) => ({
    ...z,
    orderIndex: idx + 1,
  }));
  const currentZone = zones[activeZone] || zones[0] || null;

  // Whenever the active zone changes or new analysis arrives, default activeLap to best lap
  useEffect(() => {
    if (!currentZone || !currentZone.laps || currentZone.laps.length === 0) {
      setActiveLap(null);
      setComparisonLap(null);
      return;
    }
    // Find fastest lap by lapTimeMs
    let best = currentZone.laps[0];
    for (const l of currentZone.laps) {
      if (l.lapTimeMs > 0 && (best.lapTimeMs <= 0 || l.lapTimeMs < best.lapTimeMs)) {
        best = l;
      }
    }
    setActiveLap(best);
    setComparisonLap(null);
  }, [currentZone]);

  const handleLoadAnalysis = useCallback(
    async (analysis: AnalysisRecord) => {
      const brk = analysis.braking;
      if (brk?.track) {
        const tName = brk.track.trackName || brk.track.trackId;
        setSelectedTrack(tName);
        setTrackId(brk.track.trackId);
      }
      if (brk?.sessions?.sessionIds) {
        setSelectedIds(new Set(brk.sessions.sessionIds));
      }
      setAnalyzing(true);
      setError(null);
      try {
        if (analysis.id) {
          const resp = await rpc<GetAnalysisResponse>('GetAnalysis', { id: analysis.id });
          if (resp.braking) {
            setAnalysisResult(resp.braking);
            setActiveZone(0);
            setStep(2);
          }
        } else {
          const resp = await rpc<AnalyzeResponse>('Analyze', {
            braking: brk,
            skipSave: true,
          });
          if (resp.braking) {
            setAnalysisResult(resp.braking);
            setActiveZone(0);
            setStep(2);
          }
        }
      } catch (err: any) {
        console.error('Failed to evaluate loaded braking analysis:', err);
        const isSessionMissing =
          err?.status === 404 ||
          err?.isNotFound ||
          (err?.message &&
            (err.message.includes('session') || err.message.includes('not found')));
        if (isSessionMissing) {
          setSessionRemovedAnalysis(analysis);
        } else {
          setError(err.message || 'Failed to analyze braking performance');
        }
      } finally {
        setAnalyzing(false);
      }
    },
    [setStep, setSelectedIds, setActiveZone]
  );

  // Handle restoring analysis from history navigation
  useEffect(() => {
    const stored = sessionStorage.getItem('loaded_analysis_record');
    if (stored) {
      sessionStorage.removeItem('loaded_analysis_record');
      try {
        const rec = JSON.parse(stored) as AnalysisRecord;
        if (rec.type === 'ANALYSIS_TYPE_BRAKING') {
          handleLoadAnalysis(rec);
        }
      } catch {}
    }
  }, [handleLoadAnalysis]);

  // Fetch available tracks (requiring corners for braking zones)
  useEffect(() => {
    let cancelled = false;
    rpc<ListAnalysisTracksResponse>('ListAnalysisTracks', { requireCorners: true })
      .then(resp => {
        if (!cancelled) {
          setAvailableTracks(resp.tracks || []);
        }
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch sessions and track shape for selected track
  useEffect(() => {
    if (!selectedTrack) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const listResp = await rpc<ListSessionsResponse>('ListSessions', {
          pageSize: 100,
          track_name: selectedTrack,
        });
        const filteredSessions = listResp.sessions || [];
        if (filteredSessions.length === 0) {
          setError(`${t('braking.noSessionsForTrack')} "${selectedTrack}"`);
          setLoading(false);
          return;
        }

        const detail = await rpc<SessionDetailResponse>('GetSession', {
          sessionId: filteredSessions[0].id,
        });
        const tId = detail.session.trackId;

        try {
          const shapeResp = await rpc<{
            shape: TrackShape | null;
            annotations: TrackAnnotation[];
          }>('GetTrackShapeAndAnnotations', { trackId: tId });
          if (shapeResp && !cancelled) {
            setTrackShape(shapeResp.shape || null);
            setTrackAnnotations(shapeResp.annotations || []);
          }
        } catch (err) {
          console.warn('Track shape not available:', err);
        }

        if (cancelled) return;

        setSessions(filteredSessions);
        setTrackId(tId);

        // Pre-select first session if none selected
        if (selectedIds.size === 0 && filteredSessions.length > 0) {
          updateWizardState({
            selectedIds: [filteredSessions[0].id],
            driverFilter: filteredSessions[0].driverName || 'all',
          });
        }
      } catch (e: any) {
        if (!cancelled) {
          const nf = e instanceof RpcError && e.isNotFound;
          setNotFound(nf);
          setError(
            nf
              ? 'The requested resource could not be found.'
              : e.message || 'Failed to load data'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTrack]);

  const handleAnalyze = useCallback(
    async (options?: { skipSave?: boolean }) => {
      if (selectedIds.size === 0 || !trackId) return;
      setAnalyzing(true);
      setError(null);
      try {
        const resp = await rpc<AnalyzeResponse>('Analyze', {
          skipSave: options?.skipSave,
          braking: {
            track: { trackId, trackName: selectedTrack },
            sessions: { sessionIds: [...selectedIds] },
          },
        });
        if (resp.braking) {
          setAnalysisResult(resp.braking);
          setActiveZone(0);
          setStep(2);
        }
      } catch (e: any) {
        console.error(e);
        setError(e.message || 'Failed to analyze braking zones');
      } finally {
        setAnalyzing(false);
      }
    },
    [trackId, selectedTrack, selectedIds, setActiveZone, setStep]
  );

  const handleStepClick = useCallback(
    (targetStep: number) => {
      if (targetStep < step && selectedTrack) {
        setStep(targetStep);
      }
    },
    [step, selectedTrack, setStep]
  );

  // Deep-linking into step 2
  useEffect(() => {
    if (step === 2 && !analysisResult && selectedIds.size > 0 && !analyzing && trackId) {
      handleAnalyze({ skipSave: true });
    }
  }, [step, analysisResult, selectedIds.size, analyzing, handleAnalyze, trackId]);

  const uniqueDrivers = [...new Set(sessions.map(s => s.driverName || '').filter(Boolean))].sort();

  if (loading) return <LoadingSpinner />;
  if (error && step !== 2) return <ErrorState message={error} isNotFound={notFound} />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <ToolHeader title={t('braking.title')} icon={Disc} />

      {/* Wizard stepper */}
      <div className="flex justify-center mb-6">
        <WizardStepper
          steps={wizardSteps}
          currentStep={step}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step 0: Choose Track */}
      {step === 0 && (
        <div className="max-w-2xl mx-auto">
          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b border-border-subtle">
              <h2 className="text-sm font-semibold text-text-primary">
                {t('braking.wizardChooseTrack')}
              </h2>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {availableTracks.length > 0 ? (
                availableTracks.map(track => {
                  const isSelected = draftTrack === track;
                  return (
                    <div
                      key={track}
                      onClick={() => setDraftTrack(track)}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors border-b border-border-subtle last:border-0 group ${
                        isSelected
                          ? 'bg-accent/10 border-l-2 border-l-accent'
                          : 'hover:bg-bg-hover'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => setDraftTrack(track)}
                          className="w-3.5 h-3.5 accent-accent cursor-pointer"
                        />
                        <span className="text-sm font-medium text-text-primary">{track}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-text-muted text-sm">
                  {t('braking.noTracksWithCorners')}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-border-subtle flex justify-end">
              <button
                disabled={!draftTrack}
                onClick={() => {
                  if (draftTrack) {
                    setSelectedTrack(draftTrack);
                    setStep(1);
                  }
                }}
                className={`
                  px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                  ${
                    draftTrack
                      ? 'bg-accent text-white hover:bg-accent-hover'
                      : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'
                  }
                `}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Select Sessions */}
      {step === 1 && (
        <div className="max-w-2xl mx-auto flex flex-col gap-4">
          <SessionSelector
            sessions={sessions}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            driverFilter={driverFilter}
            onDriverFilterChange={setDriverFilter}
            weatherFilter={weatherFilter}
            onWeatherFilterChange={setWeatherFilter}
            uniqueDrivers={uniqueDrivers}
          />

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setStep(0)}
              className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              <ArrowLeft size={14} />
              {t('common.back')}
            </button>

            <button
              disabled={selectedIds.size === 0 || analyzing}
              onClick={() => handleAnalyze()}
              className={`
                flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                ${
                  selectedIds.size > 0 && !analyzing
                    ? 'bg-accent text-white hover:bg-accent-hover'
                    : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'
                }
              `}
            >
              {analyzing ? <LoadingSpinner size="sm" /> : <Disc size={14} />}
              <span>{analyzing ? t('common.loading') : t('braking.title')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Braking Analysis Results */}
      {step === 2 && (
        <div className="flex flex-col gap-6">
          {error && <ErrorState message={error} />}

          {zones.length > 0 && currentZone ? (
            <>
              {/* ── Section 1: Overall Braking Performance ── */}
              <section className="space-y-4">
                <div className="flex items-center justify-between gap-4 pb-2 border-b border-border-subtle">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center text-accent">
                      <Activity size={18} />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-text-primary tracking-tight">
                        {t('braking.overallHeader')}
                      </h2>
                      <p className="text-xs text-text-muted">
                        {t('braking.overallHeaderDesc')}
                      </p>
                    </div>
                  </div>
                </div>

                <BrakingOverallHeader
                  zones={zones}
                  overallSummary={analysisResult?.overallSummary}
                  sessions={sessions}
                  selectedTrack={selectedTrack}
                  onSelectZone={setActiveZone}
                  activeZone={activeZone}
                />
              </section>

              {/* ── Section 2: Per-Corner Analysis ── */}
              <section className="space-y-4 pt-4 border-t border-border-subtle">
                <div className="flex items-center justify-between gap-4 pb-2 border-b border-border-subtle">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center text-accent">
                      <Disc size={18} />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-text-primary tracking-tight">
                        {t('braking.perCornerHeader')}
                      </h2>
                      <p className="text-xs text-text-muted">
                        {t('braking.perCornerHeaderDesc')}
                      </p>
                    </div>
                  </div>

                  {currentZone && (
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-bg-surface border border-border-subtle text-text-secondary">
                      {formatDynamicCornerLabel(
                        currentZone.name,
                        currentZone.orderIndex || activeZone + 1,
                        t('corner.turn') || 'Turn'
                      )}
                    </span>
                  )}
                </div>

                {/* Top Row: Track Map (Left) & G-G Friction Circle (Right) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                  {/* Left Column: Track Map */}
                  <div className="lg:col-span-5 flex flex-col">
                    {analysisResult?.referenceTrack && (
                      <div className="card p-4 flex flex-col items-center justify-between gap-3 h-full">
                        <div className="text-center">
                          <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
                            {selectedTrack} · {zones.length} {t('braking.zones') || 'Braking Zones'}
                          </span>
                        </div>

                        <div className="w-full max-w-sm flex-1 flex items-center justify-center">
                          <BrakingMap
                            samples={analysisResult.referenceTrack}
                            zones={zones}
                            selectedZoneIndex={activeZone}
                            onSelectZone={setActiveZone}
                            activeLap={activeLap}
                            hoveredSampleIndex={hoveredSampleIndex}
                            trackShape={trackShape}
                            trackAnnotations={trackAnnotations}
                          />
                        </div>

                        {/* Map Legend */}
                        <div className="flex items-center justify-center gap-4 py-1 px-4 rounded-full bg-bg-raised/80 border border-border-subtle/80 text-[10px] text-text-muted">
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-500 text-white flex items-center justify-center font-bold text-[7px]">
                              B
                            </div>
                            <span>{t('braking.brakePoint')}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column: GG Friction Circle Diagram */}
                  <div className="lg:col-span-7 flex flex-col">
                    <GGDiagram
                      activeLap={activeLap}
                      comparisonLap={comparisonLap}
                      allLaps={currentZone.laps}
                      hoveredSampleIndex={hoveredSampleIndex}
                      onHoverSample={setHoveredSampleIndex}
                      height={380}
                    />
                  </div>
                </div>

                {/* Telemetry Strip Chart (Graph Card) */}
                <BrakingChart
                  activeLap={activeLap}
                  comparisonLap={comparisonLap}
                  hoveredSampleIndex={hoveredSampleIndex}
                  onHoverSample={setHoveredSampleIndex}
                  height={260}
                />

                {/* Summary KPI Cards & Trail-Braking Scores (Below Graph Card) */}
                <BrakingStatsCard zone={currentZone} activeLap={activeLap} />

                {/* Bottom: Comprehensive Laps Table */}
                <BrakingLapTable
                  laps={currentZone.laps}
                  activeLap={activeLap}
                  comparisonLap={comparisonLap}
                  onSelectActiveLap={setActiveLap}
                  onSelectComparisonLap={setComparisonLap}
                />
              </section>
            </>
          ) : (
            !analyzing && (
              <div className="card p-12 text-center text-text-muted text-sm">
                {t('braking.noBrakingZones')}
              </div>
            )
          )}
        </div>
      )}

      {sessionRemovedAnalysis && (
        <SessionRemovedAnalysisDialog
          open={!!sessionRemovedAnalysis}
          onClose={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
          analysis={sessionRemovedAnalysis}
          onDeleted={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
        />
      )}
    </div>
  );
}
